import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Avatar,
  Chip,
  CircularProgress
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import api from '../services/api';

interface Supervisor {
  id: number;
  full_name: string;
  role: string;
}

interface SupervisorDropdownProps {
  value: number | '';
  onChange: (value: number | '') => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  showAvatar?: boolean;
  showRole?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: any;
}

const SupervisorDropdown: React.FC<SupervisorDropdownProps> = ({
  value,
  onChange,
  label = 'Supervisor',
  required = false,
  disabled = false,
  showAvatar = true,
  showRole = false,
  size = 'medium',
  fullWidth = true,
  sx = {}
}) => {
  const { data: supervisors = [], isLoading, error } = useQuery<Supervisor[]>(
    'supervisors',
    async () => {
      const response = await api.get('/users/supervisors');
      return response.data.users || [];
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'supervisor':
        return <PersonIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'supervisor':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <FormControl fullWidth={fullWidth} size={size} sx={sx}>
        <InputLabel>{label}</InputLabel>
        <Select
          value=""
          disabled
          label={label}
        >
          <MenuItem value="">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Loading supervisors...
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth={fullWidth} size={size} sx={sx}>
        <InputLabel>{label}</InputLabel>
        <Select
          value=""
          disabled
          label={label}
        >
          <MenuItem value="">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Error loading supervisors
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      required={required}
      disabled={disabled}
      sx={sx}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as number | '')}
        label={label}
      >
        <MenuItem value="">
          <em>Select a supervisor</em>
        </MenuItem>
        {supervisors.map((supervisor) => (
          <MenuItem key={supervisor.id} value={supervisor.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {showAvatar && (
                <Avatar sx={{ width: 24, height: 24 }}>
                  {getRoleIcon(supervisor.role)}
                </Avatar>
              )}
              <Box sx={{ flex: 1 }}>
                {supervisor.full_name}
              </Box>
              {showRole && (
                <Chip
                  label={supervisor.role}
                  color={getRoleColor(supervisor.role) as any}
                  size="small"
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SupervisorDropdown;
